import { NameBasedTargetInventoryItem } from "store/api/generatedApi";
import {
  PeriodInventoryTargets,
  InventoryPeriodGroup,
  InventoryTargetBound,
  InventoryTargetCopyScope,
  inventoryPeriodGroups,
  InventoryTargetsByPeriod,
  selectedPeriodIndices,
} from "./ruleParameters";

export const effectiveInventoryTargets = ({
  rules,
  deployable,
  forecast,
}: {
  rules: PeriodInventoryTargets[];
  deployable: number;
  forecast: number;
}): InventoryTargetsByPeriod =>
  Object.fromEntries(
    Array.from({ length: deployable + forecast }, (_, index) => {
      const period = index + 1;
      const generic = rules.flatMap((rule) =>
        !("group" in rule.periods) &&
        selectedPeriodIndices({
          selector: rule.periods,
          deployable,
          forecast,
        }).includes(period)
          ? rule.targets
          : []
      );
      const targets = new Map<string, NameBasedTargetInventoryItem>();
      generic.forEach((target) => {
        const previous = targets.get(target.material_name);
        const minima = [previous?.min_quantity, target.min_quantity].filter(
          (bound): bound is number => bound != null
        );
        const maxima = [previous?.max_quantity, target.max_quantity].filter(
          (bound): bound is number => bound != null
        );
        targets.set(target.material_name, {
          material_name: target.material_name,
          min_quantity: minima.length ? Math.max(...minima) : null,
          max_quantity: maxima.length ? Math.min(...maxima) : null,
        });
      });
      rules.forEach((rule) => {
        if (
          !("group" in rule.periods) ||
          !selectedPeriodIndices({
            selector: rule.periods,
            deployable,
            forecast,
          }).includes(period)
        )
          return;
        const { bound } = rule.periods;
        rule.targets.forEach((target) => {
          targets.set(target.material_name, {
            material_name: target.material_name,
            min_quantity: null,
            max_quantity: null,
            ...targets.get(target.material_name),
            [bound]: target[bound],
          });
        });
      });
      return [String(period), [...targets.values()]];
    })
  );

export const setGroupInventoryBound = ({
  rules,
  group,
  material,
  bound,
  quantity,
}: {
  rules: PeriodInventoryTargets[];
  group: InventoryPeriodGroup;
  material: string;
  bound: InventoryTargetBound;
  quantity: number | null;
}): PeriodInventoryTargets[] => [
  ...rules
    .map((rule) =>
      "group" in rule.periods &&
      rule.periods.group === group &&
      rule.periods.bound === bound
        ? {
            ...rule,
            targets: rule.targets.filter(
              (target) => target.material_name !== material
            ),
          }
        : rule
    )
    .filter((rule) => rule.targets.length > 0),
  {
    periods: { group, bound },
    targets: [
      {
        material_name: material,
        min_quantity: null,
        max_quantity: null,
        [bound]: quantity,
      },
    ],
  },
];

export const inventoryGroupTargets = ({
  resolved,
  deployable,
  forecast,
}: {
  resolved: InventoryTargetsByPeriod;
  deployable: number;
  forecast: number;
}): Record<InventoryPeriodGroup, NameBasedTargetInventoryItem[]> => {
  return Object.fromEntries(
    inventoryPeriodGroups.map(({ group }) => {
      const indices = selectedPeriodIndices({
        selector: { group },
        deployable,
        forecast,
      });
      return [group, resolved[String(indices[0])] ?? []];
    })
  ) as Record<InventoryPeriodGroup, NameBasedTargetInventoryItem[]>;
};

export const copyInventoryTargetBound = ({
  rules,
  material,
  bound,
  quantity,
  scope,
}: {
  rules: PeriodInventoryTargets[];
  material: string;
  bound: InventoryTargetBound;
  quantity: number | null;
  scope: InventoryTargetCopyScope;
}): PeriodInventoryTargets[] =>
  inventoryPeriodGroups
    .filter(
      ({ isDeployable }) =>
        scope === "all" || isDeployable === (scope === "deployable")
    )
    .reduce(
      (updated, { group }) =>
        setGroupInventoryBound({
          rules: updated,
          group,
          material,
          bound,
          quantity,
        }),
      rules
    );

export const materialsWithInventoryBounds = (
  targets: InventoryTargetsByPeriod
) =>
  new Set(
    Object.values(targets).flatMap((periodTargets) =>
      periodTargets
        .filter((target) =>
          [target.min_quantity, target.max_quantity].some(
            (bound) => typeof bound === "number" && Number.isFinite(bound)
          )
        )
        .map((target) => target.material_name)
    )
  );

export const inventoryClearScopes = ({
  resolved,
  material,
  deployable,
}: {
  resolved: InventoryTargetsByPeriod;
  material: string;
  deployable: number;
}): Record<InventoryTargetBound, InventoryTargetCopyScope[]> => {
  const scopesForBound = (
    bound: InventoryTargetBound
  ): InventoryTargetCopyScope[] => {
    const periods = Object.entries(resolved).filter(([, targets]) =>
      targets.some(
        (target) => target.material_name === material && target[bound] != null
      )
    );
    return [
      ...(periods.length ? ["all" as const] : []),
      ...(periods.some(([period]) => Number(period) <= deployable)
        ? ["deployable" as const]
        : []),
      ...(periods.some(([period]) => Number(period) > deployable)
        ? ["forecast" as const]
        : []),
    ];
  };
  return {
    min_quantity: scopesForBound("min_quantity"),
    max_quantity: scopesForBound("max_quantity"),
  };
};
